import { contentHeadings } from '@zuruflex/core';
import type { ContentDoc, ContentNode } from '@zuruflex/types';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn.js';

/**
 * A managed page, drawn from its stored ProseMirror JSON.
 *
 * The sibling of `TermsDocument`, for the wider vocabulary staff may write
 * in — tables, images, quotes, a third heading level. The same rules hold:
 * the tree is walked into elements rather than set as HTML, the server
 * allow-lists every node, and a node this component has no case for is
 * skipped rather than rendered, so a page written against a newer schema
 * loses a block instead of showing a blank screen.
 *
 * Headings carry an `id` — the same ones `contentHeadings` returns — so a
 * page's contents rail can link to them.
 */
export interface ContentDocumentProps {
  doc: ContentDoc;
  /** `base` for a page; `sm` where it sits inside a card, as the preview does. */
  size?: 'sm' | 'base';
  className?: string;
}

export function ContentDocument({ doc, size = 'base', className }: ContentDocumentProps) {
  const ids = contentHeadings(doc).map((heading) => heading.id);
  let cursor = 0;

  return (
    <div
      className={cn(
        'space-y-4 leading-relaxed text-text-default',
        size === 'base' ? 'text-base' : 'text-sm',
        className,
      )}
    >
      {(doc.content ?? []).map((node, index) => {
        const hasText = node.type === 'heading' && textOf(node).trim().length > 0;
        const id = hasText ? ids[cursor++] : undefined;
        return <Block key={index} node={node} id={id} />;
      })}
    </div>
  );
}

function textOf(node: ContentNode): string {
  return node.type === 'text' ? (node.text ?? '') : (node.content ?? []).map(textOf).join('');
}

function Block({ node, id }: { node: ContentNode; id?: string }): ReactNode {
  switch (node.type) {
    case 'heading': {
      const level = node.attrs?.level ?? 2;
      if (level === 4) {
        return (
          <h4 id={id} className="scroll-mt-24 pt-2 text-sm font-bold text-text-default">
            <Inline content={node.content} />
          </h4>
        );
      }
      if (level === 3) {
        return (
          <h3 id={id} className="scroll-mt-24 pt-2 text-lg font-bold text-text-default">
            <Inline content={node.content} />
          </h3>
        );
      }
      return (
        <h2 id={id} className="scroll-mt-24 pt-4 text-xl font-bold text-text-default first:pt-0">
          <Inline content={node.content} />
        </h2>
      );
    }
    case 'paragraph':
      // An empty paragraph is a deliberate blank line in the editor.
      return node.content?.length ? (
        <p>
          <Inline content={node.content} />
        </p>
      ) : null;
    case 'bulletList':
      return (
        <ul className="list-disc space-y-1 pl-5 marker:text-text-muted">
          <Items content={node.content} />
        </ul>
      );
    case 'orderedList':
      return (
        <ol className="list-decimal space-y-1 pl-5 marker:text-text-muted">
          <Items content={node.content} />
        </ol>
      );
    case 'blockquote':
      return (
        <blockquote className="space-y-2 border-l-4 border-accent/40 bg-surface-sunken py-2 pl-4 pr-3 text-text-muted">
          {(node.content ?? []).map((child, index) => (
            <Block key={index} node={child} />
          ))}
        </blockquote>
      );
    case 'horizontalRule':
      return <hr className="border-border-default" />;
    case 'image':
      return node.attrs?.src ? (
        <img
          src={node.attrs.src}
          alt={node.attrs.alt ?? ''}
          loading="lazy"
          className="mx-auto h-auto max-w-full rounded-xl border border-border-default"
        />
      ) : null;
    case 'table':
      return (
        <div className="overflow-x-auto rounded-xl border border-border-default">
          <table className="w-full border-collapse text-left text-sm">
            <tbody>
              {(node.content ?? []).map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b border-border-default last:border-b-0">
                  {(row.content ?? []).map((cell, cellIndex) => {
                    const Cell = cell.type === 'tableHeader' ? 'th' : 'td';
                    return (
                      <Cell
                        key={cellIndex}
                        colSpan={cell.attrs?.colspan}
                        rowSpan={cell.attrs?.rowspan}
                        className={cn(
                          'px-3 py-2 align-top',
                          cell.type === 'tableHeader' && 'bg-surface-sunken font-semibold',
                        )}
                      >
                        {(cell.content ?? []).map((child, childIndex) => (
                          <Block key={childIndex} node={child} />
                        ))}
                      </Cell>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

function Items({ content }: { content?: ContentNode[] }) {
  return (
    <>
      {(content ?? []).map((item, index) => (
        <li key={index} className="space-y-2">
          {(item.content ?? []).map((child, childIndex) => (
            <Block key={childIndex} node={child} />
          ))}
        </li>
      ))}
    </>
  );
}

function Inline({ content }: { content?: ContentNode[] }): ReactNode {
  return (
    <>
      {(content ?? []).map((node, index) => {
        if (node.type === 'hardBreak') return <br key={index} />;
        if (node.type !== 'text') return null;

        let rendered: ReactNode = node.text;
        const marks = node.marks ?? [];
        const href = marks.find((mark) => mark.type === 'link')?.attrs?.href;

        if (marks.some((mark) => mark.type === 'bold')) {
          rendered = <strong className="font-semibold">{rendered}</strong>;
        }
        if (marks.some((mark) => mark.type === 'italic')) rendered = <em>{rendered}</em>;
        if (marks.some((mark) => mark.type === 'underline')) rendered = <u>{rendered}</u>;
        if (href) {
          const external = /^https?:\/\//i.test(href);
          rendered = (
            <a
              href={href}
              {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="font-medium text-text-default underline decoration-accent decoration-2 underline-offset-[3px] hover:decoration-text-default"
            >
              {rendered}
            </a>
          );
        }
        return <span key={index}>{rendered}</span>;
      })}
    </>
  );
}
